/**
 * CameraCard
 *
 * Displays a single UniFi Protect camera with:
 * - Auto-refreshing snapshot image (every 30s) or live H.264 video feed
 * - Fullscreen mode (native browser Fullscreen API)
 * - Audio mute/unmute toggle for live stream
 * - "Go Live" / "Stop" toggle with quality selector (High / Medium / Low)
 * - PTZ controls overlay (pan/tilt/zoom) for PTZ-capable cameras
 * - Live motion indicator
 * - Smart detection badges
 * - Push-to-talk button (hold to talk, only on cameras with speakers)
 */

import React, { useEffect, useRef, useState, useCallback } from 'react';
import { PtzAction, UnifiCamera, useUnifiProtectStore } from '../unifi-protect-store';

interface CameraCardProps {
  camera: UnifiCamera;
}

const SNAPSHOT_REFRESH_MS = 30_000;

const QUALITY_OPTIONS: { label: string; channel: number }[] = [
  { label: 'High', channel: 0 },
  { label: 'Medium', channel: 1 },
  { label: 'Low', channel: 2 },
];

// PTZ directions and their x/y/z vectors
const PTZ_ACTIONS = [
  { action: 'tilt-up',    label: '▲', gridCol: 2, gridRow: 1 },
  { action: 'pan-left',   label: '◀', gridCol: 1, gridRow: 2 },
  { action: 'pan-right',  label: '▶', gridCol: 3, gridRow: 2 },
  { action: 'tilt-down',  label: '▼', gridCol: 2, gridRow: 3 },
] satisfies Array<{ action: PtzAction; label: string; gridCol: number; gridRow: number }>;

export function CameraCard({ camera }: CameraCardProps) {
  const { startTalkback, stopTalkback, isTalking, activeTalkback,
    startLivestream, stopLivestream, isStreaming, ptzMove, fetchSnapshot } = useUnifiProtectStore();
  const snapshotUrls = useUnifiProtectStore(s => s.nvrs[camera.nvrId]?.snapshotUrls ?? {});

  const snapshotUrl = snapshotUrls[camera.id];
  const isLive = isStreaming[camera.id] ?? false;

  const [snapshotError, setSnapshotError] = useState(false);
  const [pttError, setPttError] = useState<string | null>(null);
  const [liveError, setLiveError] = useState<string | null>(null);
  const [quality, setQuality] = useState<number>(0);
  const [isStartingLive, setIsStartingLive] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const videoContainerRef = useRef<HTMLDivElement | null>(null);

  const isTalkingThisCamera = isTalking && activeTalkback?.cameraId === camera.id;

  // PTZ cameras: canOpticalZoom flag or model name contains PTZ
  const isPtz = camera.featureFlags?.canOpticalZoom || camera.type?.toLowerCase().includes('ptz');

  // ── Snapshot refresh (only when not live) ──────────────────────────────────

  const loadSnapshot = useCallback(async () => {
    setSnapshotError(false);
    const url = await fetchSnapshot(camera.nvrId, camera.id);
    if (!url) setSnapshotError(true);
  }, [camera.nvrId, camera.id, fetchSnapshot]);

  useEffect(() => {
    if (isLive) {
      if (intervalRef.current) clearInterval(intervalRef.current);
      return;
    }
    loadSnapshot();
    intervalRef.current = setInterval(loadSnapshot, SNAPSHOT_REFRESH_MS);
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [isLive, loadSnapshot]);

  // ── Cleanup livestream on unmount ─────────────────────────────────────────

  useEffect(() => {
    return () => {
      stopLivestream(camera.id);
    };
  }, [camera.id, stopLivestream]);

  // ── Sync muted state to video element ─────────────────────────────────────

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // ── Fullscreen change listener ─────────────────────────────────────────────

  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener('fullscreenchange', onFsChange);
    return () => document.removeEventListener('fullscreenchange', onFsChange);
  }, []);

  // ── Go Live ────────────────────────────────────────────────────────────────

  const handleGoLive = async () => {
    setLiveError(null);
    setIsStartingLive(true);
    try {
      const objectUrl = await startLivestream(camera.nvrId, camera.id, quality);
      if (videoRef.current) {
        videoRef.current.src = objectUrl;
        videoRef.current.muted = isMuted;
        videoRef.current.play().catch(() => {});
      }
    } catch (err) {
      setLiveError(err instanceof Error ? err.message : 'Failed to start livestream');
    } finally {
      setIsStartingLive(false);
    }
  };

  const handleStop = () => {
    stopLivestream(camera.id);
    if (videoRef.current) {
      videoRef.current.src = '';
    }
    setLiveError(null);
    if (isFullscreen) document.exitFullscreen().catch(() => {});
  };

  const handleQualityChange = async (newChannel: number) => {
    setQuality(newChannel);
    if (isLive) {
      handleStop();
      setIsStartingLive(true);
      setLiveError(null);
      try {
        const objectUrl = await startLivestream(camera.nvrId, camera.id, newChannel);
        if (videoRef.current) {
          videoRef.current.src = objectUrl;
          videoRef.current.muted = isMuted;
          videoRef.current.play().catch(() => {});
        }
      } catch (err) {
        setLiveError(err instanceof Error ? err.message : 'Failed to restart livestream');
      } finally {
        setIsStartingLive(false);
      }
    }
  };

  // ── Fullscreen ─────────────────────────────────────────────────────────────

  const handleFullscreen = () => {
    if (isFullscreen) {
      document.exitFullscreen().catch(() => {});
    } else {
      (videoContainerRef.current || videoRef.current)?.requestFullscreen().catch(() => {});
    }
  };

  // ── Audio toggle ───────────────────────────────────────────────────────────

  const handleMuteToggle = () => {
    setIsMuted(prev => !prev);
  };

  // ── PTZ ────────────────────────────────────────────────────────────────────

  const handlePtzDown = (action: PtzAction) => {
    ptzMove(camera.nvrId, camera.id, action);
  };

  const handlePtzUp = () => {
    ptzMove(camera.nvrId, camera.id, 'stop');
  };

  // ── PTT ───────────────────────────────────────────────────────────────────

  const handlePttDown = async () => {
    if (isTalking) return;
    setPttError(null);
    try {
      await startTalkback(camera.nvrId, camera.id);
    } catch (err) {
      setPttError(err instanceof Error ? err.message : 'Push-to-talk failed');
    }
  };

  const handlePttUp = async () => {
    if (!isTalkingThisCamera) return;
    await stopTalkback();
  };

  const lastEventLabel = (() => {
    if (camera.isMotionDetected) return { label: 'Motion', color: '#f59e0b' };
    if (camera.lastRing) return { label: 'Ring', color: '#3b82f6' };
    return null;
  })();

  return (
    <div style={{
      background: 'hsl(var(--card))',
      border: `1px solid ${camera.isMotionDetected ? 'rgba(245,158,11,0.4)' : 'hsl(var(--border))'}`,
      borderRadius: 12,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      transition: 'border-color 0.2s ease',
    }}>
      {/* Video / Snapshot area */}
      <div
        ref={videoContainerRef}
        style={{ position: 'relative', aspectRatio: '16/9', background: 'hsl(var(--background))' }}
      >
        {/* Live video (hidden when not live) */}
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            width: '100%',
            height: '100%',
            objectFit: isFullscreen ? 'contain' : 'cover',
            display: isLive ? 'block' : 'none',
            background: '#000',
          }}
        />

        {/* Snapshot (hidden when live) */}
        {!isLive && (
          snapshotUrl && !snapshotError ? (
            <img
              src={snapshotUrl}
              alt={camera.name}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
              onError={() => setSnapshotError(true)}
            />
          ) : (
            <div style={{
              width: '100%', height: '100%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              color: 'hsl(var(--muted-foreground))',
              fontSize: 13,
            }}>
              {snapshotError ? 'Snapshot unavailable' : isStartingLive ? 'Connecting…' : 'Loading...'}
            </div>
          )
        )}

        {/* LIVE badge */}
        {isLive && (
          <div style={{
            position: 'absolute', top: 10, left: 10,
            display: 'flex', alignItems: 'center', gap: 5,
            background: 'rgba(239,68,68,0.9)',
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 700,
            color: '#fff',
            letterSpacing: '0.05em',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', animation: 'pulse 1s infinite' }} />
            LIVE
          </div>
        )}

        {/* Top-right controls: audio + fullscreen */}
        {isLive && (
          <div style={{
            position: 'absolute', top: 10, right: 10,
            display: 'flex', gap: 6,
          }}>
            {/* Mute/Unmute */}
            <button
              onClick={handleMuteToggle}
              title={isMuted ? 'Unmute audio' : 'Mute audio'}
              style={{
                width: 28, height: 28,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#fff',
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {isMuted ? '🔇' : '🔊'}
            </button>
            {/* Fullscreen */}
            <button
              onClick={handleFullscreen}
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              style={{
                width: 28, height: 28,
                borderRadius: '50%',
                background: 'rgba(0,0,0,0.55)',
                border: '1px solid rgba(255,255,255,0.15)',
                color: '#fff',
                fontSize: 13,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              {isFullscreen ? '✕' : '⛶'}
            </button>
          </div>
        )}

        {/* Motion indicator */}
        {camera.isMotionDetected && (
          <div style={{
            position: 'absolute', top: 10, right: isLive ? 80 : 10,
            display: 'flex', alignItems: 'center', gap: 5,
            background: 'rgba(245,158,11,0.9)',
            borderRadius: 20,
            padding: '3px 10px',
            fontSize: 11,
            fontWeight: 700,
            color: '#000',
            letterSpacing: '0.05em',
          }}>
            <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#000', animation: 'pulse 1s infinite' }} />
            MOTION
          </div>
        )}

        {/* PTZ Controls overlay (bottom-left, visible when live + PTZ camera) */}
        {isLive && isPtz && (
          <div style={{
            position: 'absolute', bottom: 10, left: 10,
            display: 'grid',
            gridTemplateColumns: 'repeat(3, 28px)',
            gridTemplateRows: 'repeat(3, 28px)',
            gap: 2,
          }}>
            {PTZ_ACTIONS.map(({ action, label, gridCol, gridRow }) => (
              <button
                key={action}
                onPointerDown={() => handlePtzDown(action)}
                onPointerUp={handlePtzUp}
                onPointerLeave={handlePtzUp}
                style={{
                  gridColumn: gridCol,
                  gridRow: gridRow,
                  background: 'rgba(0,0,0,0.6)',
                  border: '1px solid rgba(255,255,255,0.2)',
                  borderRadius: 6,
                  color: '#fff',
                  fontSize: 12,
                  cursor: 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  userSelect: 'none',
                  touchAction: 'none',
                }}
              >
                {label}
              </button>
            ))}
            {/* Zoom buttons */}
            <button
              onPointerDown={() => handlePtzDown('zoom-in')}
              onPointerUp={handlePtzUp}
              onPointerLeave={handlePtzUp}
              style={{
                gridColumn: 3, gridRow: 1,
                background: 'rgba(0,0,0,0.6)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 6,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                userSelect: 'none',
                touchAction: 'none',
              }}
            >+</button>
            <button
              onPointerDown={() => handlePtzDown('zoom-out')}
              onPointerUp={handlePtzUp}
              onPointerLeave={handlePtzUp}
              style={{
                gridColumn: 1, gridRow: 3,
                background: 'rgba(0,0,0,0.6)',
                border: '1px solid rgba(255,255,255,0.2)',
                borderRadius: 6,
                color: '#fff',
                fontSize: 14,
                fontWeight: 700,
                cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                userSelect: 'none',
                touchAction: 'none',
              }}
            >−</button>
          </div>
        )}

        {/* Offline overlay */}
        {!camera.isConnected && (
          <div style={{
            position: 'absolute', inset: 0,
            background: 'rgba(0,0,0,0.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            color: '#ef4444', fontSize: 13, fontWeight: 600,
          }}>
            Offline
          </div>
        )}
      </div>

      {/* Info */}
      <div style={{ padding: '12px 14px', flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 14, color: 'hsl(var(--foreground))' }}>
              {camera.name}
            </div>
            <div style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))', marginTop: 2 }}>
              {camera.type}
            </div>
          </div>

          {/* Smart detect badges */}
          {(camera.featureFlags?.smartDetectTypes?.length ?? 0) > 0 && (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              {(camera.featureFlags?.smartDetectTypes ?? []).map(type => (
                <span key={type} style={{
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 10,
                  background: 'rgba(99,102,241,0.15)',
                  color: '#818cf8',
                  textTransform: 'capitalize',
                  letterSpacing: '0.03em',
                }}>
                  {type}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Capability icons */}
        <div style={{ display: 'flex', gap: 8, marginTop: 10, alignItems: 'center' }}>
          {camera.hasMic && (
            <span title="Has microphone" style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>🎙️</span>
          )}
          {camera.hasSpeaker && (
            <span title="Has speaker" style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>🔊</span>
          )}
          {camera.rtspEnabled && (
            <span title="RTSP enabled" style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>📡</span>
          )}
          {isPtz && (
            <span title="PTZ camera" style={{ fontSize: 12, color: 'hsl(var(--muted-foreground))' }}>🎯</span>
          )}
          {lastEventLabel && (
            <span style={{
              fontSize: 11, fontWeight: 600,
              color: lastEventLabel.color,
              marginLeft: 'auto',
            }}>
              {lastEventLabel.label}
            </span>
          )}
        </div>

        {/* Live video controls */}
        <div style={{ marginTop: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
          {!isLive ? (
            <button
              onClick={handleGoLive}
              disabled={!camera.isConnected || isStartingLive}
              style={{
                flex: 1,
                padding: '7px 12px',
                background: camera.isConnected ? 'rgba(239,68,68,0.15)' : 'hsl(var(--border))',
                border: `1px solid ${camera.isConnected ? 'rgba(239,68,68,0.5)' : 'transparent'}`,
                borderRadius: 8,
                color: camera.isConnected ? '#f87171' : 'hsl(var(--muted-foreground))',
                fontSize: 13,
                fontWeight: 600,
                cursor: (camera.isConnected && !isStartingLive) ? 'pointer' : 'not-allowed',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                transition: 'all 0.15s ease',
              }}
            >
              {isStartingLive ? '⏳ Connecting…' : '▶ Go Live'}
            </button>
          ) : (
            <button
              onClick={handleStop}
              style={{
                flex: 1,
                padding: '7px 12px',
                background: 'rgba(239,68,68,0.8)',
                border: '1px solid #ef4444',
                borderRadius: 8,
                color: '#fff',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
                transition: 'all 0.15s ease',
              }}
            >
              ■ Stop
            </button>
          )}

          {/* Quality selector */}
          <select
            value={quality}
            onChange={(e) => handleQualityChange(Number(e.target.value))}
            style={{
              padding: '7px 8px',
              background: 'hsl(var(--border))',
              border: '1px solid transparent',
              borderRadius: 8,
              color: 'hsl(var(--muted-foreground))',
              fontSize: 12,
              cursor: 'pointer',
            }}
          >
            {QUALITY_OPTIONS.map(opt => (
              <option key={opt.channel} value={opt.channel}>{opt.label}</option>
            ))}
          </select>
        </div>

        {liveError && (
          <div style={{ fontSize: 11, color: '#f87171', marginTop: 6 }}>{liveError}</div>
        )}

        {/* Push-to-Talk button — only for cameras with speakers */}
        {camera.hasSpeaker && (
          <div style={{ marginTop: 10 }}>
            {pttError && (
              <div style={{ fontSize: 11, color: '#f87171', marginBottom: 6 }}>{pttError}</div>
            )}
            <button
              onMouseDown={handlePttDown}
              onMouseUp={handlePttUp}
              onMouseLeave={handlePttUp}
              onTouchStart={handlePttDown}
              onTouchEnd={handlePttUp}
              disabled={isTalking && !isTalkingThisCamera}
              style={{
                width: '100%',
                padding: '8px 12px',
                background: isTalkingThisCamera
                  ? 'rgba(239,68,68,0.8)'
                  : 'hsl(var(--border))',
                border: `1px solid ${isTalkingThisCamera ? '#ef4444' : 'transparent'}`,
                borderRadius: 8,
                color: isTalkingThisCamera ? '#fff' : 'hsl(var(--muted-foreground))',
                fontSize: 13,
                fontWeight: 600,
                cursor: (isTalking && !isTalkingThisCamera) ? 'not-allowed' : 'pointer',
                userSelect: 'none',
                transition: 'all 0.1s ease',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 6,
              }}
            >
              <span>{isTalkingThisCamera ? '🔴' : '🎤'}</span>
              {isTalkingThisCamera ? 'Talking... (release to stop)' : 'Hold to Talk'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
